import React, { useState, useEffect } from 'react';
import { BLOG_POSTS, BlogPost } from '../data/blogs';
import { useSiteImages } from '../context/SiteImagesContext';
import { SafeImage } from '../components/SafeImage';
import { PageHeaderBanner } from '../components/PageHeaderBanner';
import { buildCleanPath } from '../utils/navigation';
import {
  Search,
  BookOpen,
  Clock,
  Calendar,
  Eye,
  Heart,
  ArrowRight,
  TrendingUp,
  Tag,
  Share2,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  ExternalLink
} from 'lucide-react';

interface BlogPageProps {
  onNavigate: (page: string, param?: string) => void;
}

export const BlogPage: React.FC<BlogPageProps> = ({ onNavigate }) => {
  const { images } = useSiteImages();
  const [blogs, setBlogs] = useState<BlogPost[]>(BLOG_POSTS);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // SEO Metadata Update
  useEffect(() => {
    document.title = 'Blog & Market Insights | Saints Tech Solutions Kenya';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Explore in-depth technical guides, dairy farming automation, water purification breakthroughs, and commercial vending market trends across Kenya by Saints Tech Solutions.'
      );
    }

    // Inject CollectionPage Schema.org JSON-LD
    const scriptId = 'blog-collection-ld-json';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': 'Saints Tech Solutions Blog & Industry Insights',
      'description': 'Technical analysis and market blueprints for dairy farming, water purification, and automated vending in Kenya.',
      'url': 'https://saintstech.co.ke/blog',
      'publisher': {
        '@type': 'Organization',
        'name': 'Saints Tech Solutions',
        'logo': 'https://saintstech.co.ke/logo.png'
      }
    });

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  // Fetch from backend API
  useEffect(() => {
    let isMounted = true;
    const fetchBlogs = async () => {
      try {
        const queryParams = new URLSearchParams();
        if (selectedCategory !== 'all') queryParams.append('category', selectedCategory);
        if (searchQuery.trim()) queryParams.append('search', searchQuery.trim());

        const res = await fetch(`/api/blogs?${queryParams.toString()}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && Array.isArray(data.blogs) && isMounted) {
            setBlogs(data.blogs);
          }
        }
      } catch (err) {
        // Fallback to local data
        console.warn('Backend blog fetch fallback active', err);
      }
    };

    fetchBlogs();
    return () => {
      isMounted = false;
    };
  }, [selectedCategory, searchQuery]);

  const categories = [
    { id: 'all', label: 'All Insights' },
    { id: 'Commercial Automation', label: 'Commercial Automation' },
    { id: 'Dairy Technology', label: 'Dairy Technology' },
    { id: 'Water Purification', label: 'Water Purification' },
    { id: 'Urban Vending', label: 'Urban Vending' }
  ];

  const featuredPost = blogs.find((b) => b.featured) || blogs[0];
  const regularPosts = blogs.filter((b) => b.id !== featuredPost?.id);

  const handlePostClick = (slug: string, e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    onNavigate('blog-detail', slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Page Title Header with Duotone Background (matching example) */}
      <PageHeaderBanner
        breadcrumb="Blog & Insights"
        parentPage={{ label: 'Home', page: 'home' }}
        badgeIcon={<Sparkles className="w-3.5 h-3.5" />}
        badgeText="Saints Tech Technical Engineering & Field Notes"
        badgeColor="emerald"
        title="Knowledge, Blueprints & Market Insights"
        description="In-depth research on cold-chain dairy processing, Reverse Osmosis water science, and automated micro-dispensing economics across Kenya."
        backgroundImage={images.blogBanner}
        gradientTheme="emerald-amber"
        onNavigate={onNavigate}
      />

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        {/* Search & Category Filter Bar */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200/90 shadow-2xs space-y-4">
          <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
            <div className="relative flex-1 w-full">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search articles by topic, machine type, or county (e.g. Milk ATM, Reverse Osmosis, Nairobi)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full text-xs sm:text-sm pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-sky-500"
              />
            </div>

            <div className="text-xs text-slate-500 font-medium whitespace-nowrap">
              Showing <strong className="text-slate-900 tabular-nums">{blogs.length}</strong> published technical analyses
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  selectedCategory === cat.id
                    ? 'bg-sky-600 text-white shadow-xs'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Editorial Post */}
        {featuredPost && !searchQuery && selectedCategory === 'all' && (
          <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm overflow-hidden hover:border-slate-300 transition-all group">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
              {/* Media Section */}
              <div className="lg:col-span-7 relative overflow-hidden bg-slate-900 min-h-[300px] lg:min-h-[420px]">
                <SafeImage
                  src={featuredPost.image}
                  fallbackCategory="general"
                  alt={featuredPost.title}
                  className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4 bg-sky-600 text-white text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full shadow-md">
                  Featured Lead Story
                </div>
              </div>

              {/* Text Section */}
              <div className="lg:col-span-5 p-6 sm:p-8 lg:p-10 flex flex-col justify-between space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="font-bold text-sky-700 uppercase tracking-wider text-[11px]">
                      {featuredPost.category}
                    </span>
                    <span aria-hidden="true">·</span>
                    <span>{featuredPost.publishedDate}</span>
                    <span aria-hidden="true">·</span>
                    <span>{featuredPost.readTime}</span>
                  </div>

                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-snug group-hover:text-sky-700 transition-colors">
                    <a
                      href={buildCleanPath('blog-detail', featuredPost.slug)}
                      onClick={(e) => handlePostClick(featuredPost.slug, e)}
                    >
                      {featuredPost.title}
                    </a>
                  </h2>

                  <p className="text-sm text-slate-600 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>

                  {/* Highlights Pill-free Box */}
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-2">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                      Engineering Takeaway:
                    </div>
                    <p className="text-xs text-slate-600 italic leading-relaxed">
                      "{featuredPost.keyTakeaways[0]}"
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-sky-100 text-sky-800 font-bold text-xs flex items-center justify-center">
                      {featuredPost.author.avatarInitials}
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-900">{featuredPost.author.name}</div>
                      <div className="text-[10px] text-slate-500 truncate max-w-[180px]">
                        {featuredPost.author.role}
                      </div>
                    </div>
                  </div>

                  <a
                    href={buildCleanPath('blog-detail', featuredPost.slug)}
                    onClick={(e) => handlePostClick(featuredPost.slug, e)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-sky-600 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
                  >
                    <span>Read Analysis</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Regular Articles Grid */}
        <div className="space-y-6">
          <div className="flex items-center justify-between border-b border-slate-200 pb-3">
            <h3 className="text-lg font-bold text-slate-900">
              {searchQuery ? `Search Results for "${searchQuery}"` : 'All Technical Articles & Field Guides'}
            </h3>
            <span className="text-xs text-slate-500 font-medium">
              Real-world Kenyan case studies & specifications
            </span>
          </div>

          {blogs.length === 0 ? (
            <div className="bg-white rounded-3xl p-16 text-center border border-slate-200 max-w-md mx-auto space-y-3">
              <BookOpen className="w-10 h-10 text-slate-300 mx-auto" />
              <h4 className="text-base font-bold text-slate-900">No articles matched your search</h4>
              <p className="text-xs text-slate-500">
                Try searching for other terms like "Milk ATM", "Reverse Osmosis", "Pasteurizer", or "Salad Oil".
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                className="px-4 py-2 bg-sky-600 text-white text-xs font-semibold rounded-xl"
              >
                Clear Search Filter
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(searchQuery || selectedCategory !== 'all' ? blogs : regularPosts).map((post) => (
                <article
                  key={post.id}
                  className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs overflow-hidden flex flex-col hover:shadow-md hover:border-slate-300 transition-all group"
                >
                  {/* Article Image Container */}
                  <a
                    href={buildCleanPath('blog-detail', post.slug)}
                    onClick={(e) => handlePostClick(post.slug, e)}
                    className="block relative aspect-video overflow-hidden bg-slate-900"
                  >
                    <SafeImage
                      src={post.image}
                      fallbackCategory="general"
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3 bg-slate-900/80 backdrop-blur-xs text-white text-[10px] font-semibold px-2.5 py-1 rounded-md">
                      {post.readTime}
                    </div>
                  </a>

                  {/* Body Content */}
                  <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="font-bold text-sky-700 uppercase tracking-wider text-[10px]">
                          {post.category}
                        </span>
                        <span aria-hidden="true">·</span>
                        <span>{post.publishedDate}</span>
                      </div>

                      <h4 className="text-base font-bold text-slate-900 leading-snug group-hover:text-sky-700 transition-colors line-clamp-2">
                        <a
                          href={buildCleanPath('blog-detail', post.slug)}
                          onClick={(e) => handlePostClick(post.slug, e)}
                        >
                          {post.title}
                        </a>
                      </h4>

                      <p className="text-xs text-slate-600 leading-relaxed line-clamp-3">
                        {post.excerpt}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-700">{post.author.name}</span>
                      </div>

                      <a
                        href={buildCleanPath('blog-detail', post.slug)}
                        onClick={(e) => handlePostClick(post.slug, e)}
                        className="inline-flex items-center gap-1 font-bold text-sky-700 hover:text-sky-800 transition-colors"
                      >
                        <span>Read</span>
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* Technical Consultation CTA Banner */}
        <div className="bg-gradient-to-r from-sky-900 via-slate-900 to-indigo-950 text-white rounded-3xl p-8 sm:p-12 border border-slate-800 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <span className="text-xs uppercase font-bold text-sky-400 tracking-wider">
              Nairobi Engineering Consultation
            </span>
            <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Have a specific equipment or water question?
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              Our technicians at Juja Road Green Mall offer free water testing analysis, custom equipment dimensioning, and ROI feasibility reports.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <button
              onClick={() => onNavigate('calculator')}
              className="px-5 py-3 bg-sky-600 hover:bg-sky-500 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer shadow-md"
            >
              Launch ROI Calculator
            </button>
            <button
              onClick={() => onNavigate('contact')}
              className="px-5 py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white rounded-xl text-xs font-bold border border-slate-700 transition-colors cursor-pointer"
            >
              Contact Engineering Desk
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
